import { BaseQueryFn, FetchArgs, fetchBaseQuery, FetchBaseQueryError } from '@reduxjs/toolkit/query'
import { Mutex } from 'async-mutex'
import { AuthTokenArgs } from '@typesApp/Session'
import { AppState } from '@redux/store'
import { config } from '@config/config'
import { hasCognitoSession, isAuthenticationError, isSessionValid } from '@utils/authToken'
import { sessionsApi } from './sessions'
import { dbPrepareHeaders } from './prepareHeaders'
import { authApi } from './auth'
import { ApiCache } from './apiCache'

const mutex = new Mutex()

export const dbBaseQueryWithReauth: BaseQueryFn<string | FetchArgs, unknown, FetchBaseQueryError> = async (
  args,
  api,
  extraOptions
) => {
  const baseUrl = config.getApiHost()
  const dbBaseQuery = fetchBaseQuery({
    baseUrl,
    prepareHeaders: dbPrepareHeaders,
  })

  const relativeUrl = typeof args === 'string' ? args : args.url
  const url = `${baseUrl}${relativeUrl}`
  const apiCache = new ApiCache(api.endpoint)

  // fetch from cache if available
  if (apiCache.isCacheable) {
    const cachedResult = await apiCache.fetchFromCache(url)
    if (cachedResult) {
      return cachedResult
    }
  }

  // wait until the mutex is available without locking it
  await mutex.waitForUnlock()

  let result = await dbBaseQuery(args, api, extraOptions)

  if (isAuthenticationError(result) && isSessionValid(api.getState())) {
    // checking whether the mutex is locked
    if (!mutex.isLocked()) {
      const release = await mutex.acquire()

      try {
        // Refresh token with latest session id
        const context = await api.dispatch(
          sessionsApi.endpoints.getCustomerContext.initiate(undefined, { subscribe: true, forceRefetch: true })
        )
        const sessionId = context.data?.sessionId ?? ''

        // Refresh customer session information for request header
        await api.dispatch(
          sessionsApi.endpoints.getSession.initiate(sessionId, { subscribe: true, forceRefetch: true })
        )

        const state = api.getState() as AppState
        const customer = state.connector.sessionCustomer

        const authTokenArgs: AuthTokenArgs = {
          sessionId,
          customerNumber: customer?.id ?? '',
          customerEmail: customer?.email,
        }

        // fetch will set the local auth token if successful
        const response = await api.dispatch(authApi.endpoints.getAuthToken.initiate(authTokenArgs))

        if ('data' in response && hasCognitoSession(response.data)) {
          // Retry original request with new token
          result = await dbBaseQuery(args, api, extraOptions)
        } else {
          console.error('directbilling - session expired or logged out', response)
          result = { error: response['error'] as FetchBaseQueryError }
        }
      } finally {
        release()
      }
    } else {
      // wait until the mutex is available without locking it
      await mutex.waitForUnlock()
      result = await dbBaseQuery(args, api, extraOptions)
    }
  }

  if (apiCache.isCacheable) {
    apiCache.putInCache(result)
  }

  return result
}
